import { ResetPasswordResponse } from "aws-sdk/clients/workmail";
import { useRouter } from "next/router";
import { useDispatch } from "react-redux";
import { routes } from "../../../config/route-config";
import {
  useForgotPasswordMutation,
  useLoginMutation,
  useResetPasswordMutation,
  useVerifyAdminMutation
} from "../../../lib/api/endpoints/Auth/authEndpoints";
import { setCredentials } from "../../../lib/redux/slices/authSlice";
import { LoginTypes } from "../../../lib/types/LoginTypes";
import {
  ForgotPasswordRequest,
  LoginResponse,
  v3ForgotPasswordResponse,
  v3VerifyAdminResponse
} from "../../../lib/types/auth";
import { ForgetPasswordTypes } from "../../../lib/types/pageTypes/Auth/ForgetPasswordTypes";
import { ResetPasswordTypes } from "../../../lib/types/pageTypes/Auth/ResetPasswordTypes";
import v3HandleAPIRequests from "../../../utils/v3.handleAPIRequests";

/**
 * @since February 2024
 * @author Issa Jean Marie <jeanmarieissa@gmail.com>
 * @see {@link https://issadevs.com} - Author's website
 */

export const useV3HandleLogin = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const [login, res] = useLoginMutation();

  const onSuccess = (res: LoginResponse) => {
    if (res.payload) {
      dispatch(setCredentials(res));
      router.push(routes.Orders.url);
    }
  };

  const onFinish = (values: LoginTypes) => {
    v3HandleAPIRequests<LoginTypes, LoginResponse>({
      request: login,
      onSuccess,
      ...values
    });
  };

  return { ...res, onFinish };
};

export const useV3HandleResetPassword = () => {
  const router = useRouter();
  const query = router.query;
  const [resetPassword, res] = useResetPasswordMutation();

  const onSuccess = () => {
    router.replace(routes.login.url);
  };

  const onFinish = (values: ResetPasswordTypes) => {
    const requestBody = { password: values?.password, token: query?.token };
    v3HandleAPIRequests<ResetPasswordTypes, ResetPasswordResponse>({
      request: resetPassword,
      onSuccess,
      ...requestBody
    });
  };

  return { ...res, onFinish };
};

export const useV3HandleForgotPassword = () => {
  const [forgotPassword, res] = useForgotPasswordMutation();
  const router = useRouter();

  const onFinish = (values: ForgetPasswordTypes) => {
    const onSuccess = () => {
      router.replace(routes.ResetPasswordSent.url);
    };

    const requestBody = {
      username: values?.username,
      fromFinance: false,
      fromApp: true
    };

    v3HandleAPIRequests<ForgotPasswordRequest, v3ForgotPasswordResponse>({
      request: forgotPassword,
      onSuccess,
      ...requestBody
    });
  };

  return { ...res, onFinish };
};

export const useV3HandleVerifyAccount = () => {
  const router = useRouter();
  const { token } = router.query;
  const [verifyAccount, res] = useVerifyAdminMutation();

  const onSuccess = () => {
    router.replace(routes.login.url);
  };

  const onFailure = () => {
    router.replace(routes.login.url);
  };

  const onFinish = (values: ResetPasswordTypes) => {
    const requestBody = {
      userType:
        router?.query?.accountType !== "agent" ? "ADMIN" : "ADMIN_GUEST",
      ...values,
      token
    };

    v3HandleAPIRequests<ResetPasswordTypes, v3VerifyAdminResponse>({
      request: verifyAccount,
      onSuccess,
      onFailure,
      ...requestBody
    });
  };

  return { ...res, onFinish };
};
